import React, { useRef, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import centroid from '@turf/centroid'
import nearestPointOnLine from '@turf/nearest-point-on-line'
import polygonToLine from '@turf/polygon-to-line'

const StreetView = ({ activeFeature }) => {
    const [initialPanoLocation, setInitialPanoLocation] = useState(null)
    const [panorama, setPanorama] = useState(null) // instantiation of Google's StreetViewPanorama class
    const [error, setError] = useState(false)
    const streetViewRef = useRef()
    const activeCentroidCoords = centroid(activeFeature).geometry.coordinates
    const activeCentroid = { lng: activeCentroidCoords[0], lat: activeCentroidCoords[1] }

    const latLngGoogle = coords => {
        return new window.google.maps.LatLng(
            parseFloat(coords[0]), // lat
            parseFloat(coords[1]) // lng
        )
    }

    const getPovFromLocation = location => {
        const locationGeoJSON = {
            type: 'Feature',
            geometry: {
                type: 'Point',
                coordinates: [location.lng, location.lat]
            },
            properties: {}
        }
        const activeFeatureLines = polygonToLine(activeFeature)
        const nearestPointOnPolygon = nearestPointOnLine(activeFeatureLines, locationGeoJSON).geometry.coordinates
        const heading = window.google.maps.geometry.spherical.computeHeading(
            latLngGoogle([location.lat, location.lng]),
            latLngGoogle([nearestPointOnPolygon[1], nearestPointOnPolygon[0]])
        )
        return { heading, pitch: 0 }
    }

    const findPanoLocation = latLng => {
        if (!window.google) {
            return null
        }

        const callback = (data, status) => {
            if (status === 'ZERO_RESULTS') {
                setError(false)
                setPanorama(null)
                return setInitialPanoLocation('noneAvailable')
            }
            if (status !== 'OK') {
                setError(true)
                setPanorama(null)
                return setInitialPanoLocation(null)
            }
            const location = { lat: data.location.latLng.lat(), lng: data.location.latLng.lng() }
            const pov = getPovFromLocation(location)
            if (!panorama) {
                setPanorama(
                    new window.google.maps.StreetViewPanorama(streetViewRef.current, {
                        fullscreenControl: false,
                        addressControl: false,
                        panControl: false,
                        controlSize: 30,
                        position: location,
                        pov
                    })
                )
            } else {
                panorama.setPosition(location)
                panorama.setPov(pov)
            }
            setError(false)
            return setInitialPanoLocation(location)
        }

        const streetViewService = new window.google.maps.StreetViewService()
        return streetViewService.getPanorama({ location: latLng }, callback)
    }

    useEffect(() => {
        // Runs once on initialization
        if (!window.google) {
            const googleStreetScript = document.createElement('script')
            googleStreetScript.src = `https://maps.googleapis.com/maps/api/js?key=${window.SETTINGS.gsv_key}&libraries=geometry`
            window.document.body.appendChild(googleStreetScript)
            googleStreetScript.addEventListener('load', () => findPanoLocation(activeCentroid))
        } else if (!initialPanoLocation) {
            findPanoLocation(activeCentroid)
        }

        // Runs once on cleanup
        return () => {
            setInitialPanoLocation(null)
            setPanorama(null)
        }
    }, [])

    // update location when active feature changes
    useEffect(() => {
        findPanoLocation(activeCentroid)
    }, [activeFeature.properties.parcel_id])

    let content = ''
    if (!initialPanoLocation || !panorama) {
        content = 'Loading...'
    }
    if (initialPanoLocation === 'noneAvailable') {
        content = 'Unfortunately, street view is not currently available for the selected parcel.'
    }
    if (error) {
        content = 'There was an error loading streetview.'
    }
    return (
        <div
            ref={streetViewRef}
            className={`street-view-container ${initialPanoLocation === 'noneAvailable' || error ? 'text-only' : ''}`}
        >
            {content}
        </div>
    )
}

StreetView.propTypes = {
    activeFeature: PropTypes.shape({
        geometry: PropTypes.shape({
            type: PropTypes.string.isRequired,
            coordinates: PropTypes.array.isRequired
        }),
        type: PropTypes.string.isRequired,
        properties: PropTypes.shape({
            parcel_id: PropTypes.string.isRequired
        })
    }).isRequired
}

export default StreetView
